import { useState } from "react";
import { Phone, Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    moving_from: "",
    moving_to: "",
    moving_date: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const serviceTypes = [
    "House Moving",
    "Office Relocation",
    "Packing Services",
    "Vehicle Transportation",
    "Storage Solutions",
    "Furniture Assembly",
    "Catering Equipment Transport",
    "Restaurant Relocation Logistics",
    "Food Service Delivery",
    "Cleaning Equipment Transport",
    "Light Clearance & Removal Support",
    "IT Equipment Transport",
    "Office Technology Logistics",
    "Other Logistics Services"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact-form`, formData);
      console.log("Form submitted successfully:", response.data);
      setSubmitStatus("success");
      
      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        service_type: "",
        moving_from: "",
        moving_to: "",
        moving_date: "",
        message: ""
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="contact-hero-title">
            Get Your Free Quote Today
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-4">
            Whether you need moving services or specialized logistics for your industry - we're here to help
          </p>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Our friendly team responds within 2 hours during business hours
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-testid="contact-info-heading">
                Contact Information
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Reach out to us and we'll respond as soon as possible. We're here to help with all your moving needs. Our team is dedicated to providing excellent customer service and ensuring your move is stress-free.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Head Office</h3>
                    <p className="text-gray-600" data-testid="contact-info-address">
                      Innovation Centre and Business Base<br />
                      110 Butterfield, Great Marlings<br />
                      Luton, LU2 8DL<br />
                      United Kingdom
                    </p>
                    <p className="text-sm text-blue-600 mt-2 font-medium">Operating across the UK</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a 
                      href="tel:+447405747177" 
                      data-testid="contact-info-phone"
                      className="text-gray-600 hover:text-blue-600 transition-colors text-lg"
                    >
                      +44 7405 747177
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Available 7 days a week</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a 
                      href="mailto:marunsltd@outlook.com" 
                      data-testid="contact-info-email"
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      marunsltd@outlook.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">We respond within 2 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-600" data-testid="contact-info-hours">
                      Monday - Saturday: 8:00 AM - 8:00 PM<br />
                      Sunday: 9:00 AM - 6:00 PM
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Emergency moves available 24/7</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Why Contact Us?</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Free, no-obligation quotes</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Expert advice and planning</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Transparent pricing</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>Fast response times</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-testid="contact-form-heading">
                Request a Free Quote
              </h2>

              {submitStatus === "success" && (
                <div 
                  data-testid="form-success-message"
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800">Thank you! We'll contact you soon.</span>
                </div>
              )}

              {submitStatus === "error" && (
                <div 
                  data-testid="form-error-message"
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800"
                >
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="form-input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        data-testid="form-input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="form-input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service_type" className="block text-sm font-medium text-gray-700 mb-1">
                      Service Type *
                    </label>
                    <select
                      id="service_type"
                      name="service_type"
                      data-testid="form-select-service-type"
                      value={formData.service_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a service</option>
                      {serviceTypes.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="moving_from" className="block text-sm font-medium text-gray-700 mb-1">
                        Moving From *
                      </label>
                      <input
                        type="text"
                        id="moving_from"
                        name="moving_from"
                        data-testid="form-input-moving-from"
                        value={formData.moving_from}
                        onChange={handleChange}
                        required
                        placeholder="City/Location"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label htmlFor="moving_to" className="block text-sm font-medium text-gray-700 mb-1">
                        Moving To *
                      </label>
                      <input
                        type="text"
                        id="moving_to"
                        name="moving_to"
                        data-testid="form-input-moving-to"
                        value={formData.moving_to}
                        onChange={handleChange}
                        required
                        placeholder="City/Location"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="moving_date" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Moving Date *
                    </label>
                    <input
                      type="date"
                      id="moving_date"
                      name="moving_date"
                      data-testid="form-input-moving-date"
                      value={formData.moving_date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="form-textarea-message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us more about your moving requirements..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
